import * as React from "react";
import {
  Avatar,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Rating,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
  alpha,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Link,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetTalentByIdQuery,
  useGetTalentTotalEarnedByIdQuery,
  useGetTalentTotalJobHiredByIdQuery,
  useGetTalentTotalJobSuccessRateByIdQuery,
} from "../../services/talent.service";
import {
  useGetAllFavoriteTalentsQuery,
  useAddFavoriteTalentMutation,
  useDeleteFavoriteTalentMutation,
} from "../../services/favoriteTalent.service";
import { useGetUserDocumentsQuery } from "../../services/document.service";
import { useTitle } from "../../hooks/useTitle";
import PersonPinCircleIcon from "@mui/icons-material/PersonPinCircle";
import FavoriteIcon from "@mui/icons-material/Favorite";
import EmailIcon from "@mui/icons-material/Email";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import { Talent, UserWorkStatus } from "../../types/users";
import TalentProfilePanel from "./TalentProfilePanel";
import { formatCash } from "../../utils/helper";
import { toast } from "react-toastify";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Favorite,
  FavoriteOutlined,
  Info,
  More,
  MoreVert,
  FilePresent,
  Description,
} from "@mui/icons-material";
import { CardBase } from "../../components/global";
import dayjs from "dayjs";
import { useUILabel } from "../../hooks/useUILabel";

// declare var ActiveXObject: {
//   new (s: string): any;
// }

const MoreMenu = ({ talent }: { talent?: Talent; }) => {
  const navigate = useNavigate();
  const reviewsLabel = useUILabel("talent.reviews");
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div>
      <IconButton
        id="basic-button"
        aria-controls={open ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        sx={{
          ml: { xs: 0, sm: 1 },
          border: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <MoreVert />
      </IconButton>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        <MenuItem
          onClick={() =>
            navigate(`/independent-professionals/${talent?.id}/reviews`)
          }
        >
          {reviewsLabel}
        </MenuItem>
      </Menu>
    </div>
  );
};

const ProfileStat = ({
  label,
  value,
  isLoading,
  onClick,
}: {
  label: string;
  value: string;
  isLoading?: any;
  onClick?: () => void;
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: "12px",
        py: { xs: 0.75, sm: 1 },
        px: { xs: 1, sm: 1.5 },
        alignItems: "center",
        mr: { xs: 0.5, sm: 1 },
        mb: { xs: 0.5, sm: 0 },
        minWidth: { xs: 70, sm: 80 },
        cursor: onClick ? "pointer" : "default",
        background: isDark
          ? `linear-gradient(135deg, ${alpha("#ffffff", 0.15)} 0%, ${alpha("#ffffff", 0.08)} 100%)`
          : `linear-gradient(135deg, ${alpha("#ffffff", 0.25)} 0%, ${alpha("#ffffff", 0.15)} 100%)`,
        backdropFilter: "blur(20px) saturate(180%)",
        WebkitBackdropFilter: "blur(20px) saturate(180%)",
        border: `1.5px solid ${isDark ? alpha("#ffffff", 0.3) : alpha("#ffffff", 0.6)}`,
        boxShadow: isDark
          ? "0 4px 16px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)"
          : "0 4px 16px rgba(18, 144, 60, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.5)",
        transition: "all 0.3s ease",
        "&:hover": onClick
          ? {
            transform: "translateY(-2px)",
            boxShadow: isDark
              ? "0 6px 20px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)"
              : "0 6px 20px rgba(18, 144, 60, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.5)",
          }
          : {},
      }}
      onClick={onClick}
    >
      {isLoading ? (
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress size={16} />
        </Box>
      ) : (
        <Typography textAlign={"center"} fontSize={{ xs: 14, sm: 16 }} fontWeight={600}>
          {value}
        </Typography>
      )}
      <Typography textAlign={"center"} fontSize={{ xs: 10, sm: 12 }} color="text.secondary" sx={{ mt: 0.5 }}>
        {label}
      </Typography>
    </Box>
  );
};

const ProfileMeta = ({
  icon,
  value,
  onClick,
}: {
  icon: React.ReactNode;
  value: string;
  onClick?: () => void;
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Button
      onClick={onClick}
      variant="text"
      startIcon={icon}
      size="small"
      sx={{
        mr: { xs: 1, sm: 2 },
        mb: { xs: 0.5, sm: 0 },
        textTransform: "unset",
        color: "text.secondary",
        borderRadius: "8px",
        px: { xs: 1, sm: 1.5 },
        py: 0.5,
        fontSize: { xs: "0.75rem", sm: "0.875rem" },
        transition: "all 0.2s ease",
        "&:hover": {
          background: isDark
            ? alpha("#ffffff", 0.1)
            : alpha("#12903C", 0.08),
          color: "#12903C",
        },
      }}
    >
      {value}
    </Button>
  );
};

const ProfileHeader = ({
  data,
  dataTotalJobSuccessRate,
  dataTotalJobHired,
  dataTotalEarned,
  isLoadingTotalJobSuccessRate,
  isLoadingTotalJobHired,
  isLoadingTotalEarned,
  addingFavorite,
  deletingFavorite,
  isFavorite,
  onAddFav,
  onDeleteFav,
}: {
  data?: Talent;
  dataTotalJobSuccessRate: any;
  dataTotalJobHired: any;
  dataTotalEarned: any;
  isLoadingTotalJobSuccessRate: boolean;
  isLoadingTotalJobHired: boolean;
  isLoadingTotalEarned: boolean;
  addingFavorite: boolean;
  deletingFavorite: boolean;
  isFavorite: boolean;
  onAddFav: () => void;
  onDeleteFav: () => void;
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const favoritedLabel = useUILabel("talent.favorited");
  const addAsFavoriteLabel = useUILabel("talent.addAsFavorite");
  const unknownCityLabel = useUILabel("talent.unknownCity");
  const unknownStateLabel = useUILabel("talent.unknownState");
  const noEmailLabel = useUILabel("talent.noEmail");
  const noPhoneLabel = useUILabel("talent.noPhone");
  const memberSinceLabel = useUILabel("talent.memberSince");
  const ratingLabel = useUILabel("talent.rating");
  const workSuccessLabel = useUILabel("talent.workSuccess");
  const workLabel = useUILabel("talent.work");
  const totalEarnedLabel = useUILabel("talent.totalEarned");
  const workStatusLabel = useUILabel("talent.workStatus");
  const soleProprietorLabel = useUILabel("talent.soleProprietor");
  const entrepreneurLabel = useUILabel("talent.entrepreneur");
  const partTimeLabel = useUILabel("talent.partTime");
  const notSetLabel = useUILabel("talent.notSet");

  return (
    <CardBase useGlassCard={true} nested={false} sx={{ mb: 3, p: { xs: 2, sm: 3 } }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={{ xs: 2, sm: 2 }}
      >
        <Avatar
          variant="square"
          sx={{
            bgColor: "primary.main",
            width: { xs: 80, sm: 100 },
            height: { xs: 80, sm: 100 },
            mr: { xs: 0, sm: 2 },
            mb: { xs: 1, sm: 0 },
            border: `2px solid ${isDark ? alpha("#ffffff", 0.3) : alpha("#ffffff", 0.6)}`,
            boxShadow: isDark
              ? "0 4px 16px rgba(0, 0, 0, 0.2)"
              : "0 4px 16px rgba(18, 144, 60, 0.1)",
          }}
          alt={data?.first_name || "Talent"}
          src={
            (!!data?.photo?.length && data?.photo[0].original_url) ||
            "broken-image.jpg"
          }
        />
        <Stack direction="column" flex={1} sx={{ width: { xs: "100%", sm: "auto" } }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            sx={{ mb: 1 }}
            spacing={{ xs: 0.5, sm: 0 }}
          >
            <Typography
              variant="h5"
              sx={{
                color: "#12903C",
                fontWeight: 700,
                fontSize: { xs: "1.25rem", sm: "1.5rem" }
              }}
            >
              {data?.full_name}
            </Typography>
            <Rating
              value={data?.total_rating || 0}
              readOnly
              sx={{ ml: { xs: 0, sm: 2 }, mt: { xs: 0.5, sm: 0 } }}
              precision={0.5}
              size={isMobile ? "small" : "medium"}
            />
          </Stack>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            sx={{ mb: 2 }}
            flexWrap="wrap"
            spacing={{ xs: 1, sm: 0 }}
          >
            <ProfileMeta
              icon={<PersonPinCircleIcon />}
              value={`${data?.city || unknownCityLabel}, ${data?.state || unknownStateLabel}`}
            />
            <ProfileMeta
              icon={<EmailIcon />}
              value={`${data?.email || noEmailLabel}`}
              onClick={() => {
                window.location.href = `${`mailto:${data?.email}`}`;
              }}
            />
            <ProfileMeta
              icon={<PhoneAndroidIcon />}
              value={`${data?.phone_number || noPhoneLabel}`}
              onClick={() => {
                window.location.href = `${`TEL:${data?.phone_number}`}`;
              }}
            />
            <ProfileMeta
              icon={<Info />}
              value={`${memberSinceLabel}: ${dayjs(data?.created_at).format("MM/YY")}`}
            />
          </Stack>
          <Stack
            direction="row"
            sx={{ mt: 1 }}
            flexWrap="wrap"
            spacing={{ xs: 1, sm: 0 }}
            useFlexGap
          >
            <ProfileStat
              label={ratingLabel}
              value={`${+Number(data?.total_rating || 0).toFixed(2)}`}
              onClick={() =>
                navigate(`/independent-professionals/${data?.id}/reviews`)
              }
            />
            <ProfileStat
              label={workSuccessLabel}
              value={`${+Number(dataTotalJobSuccessRate?.total || 0).toFixed(
                2
              )}%`}
              isLoading={isLoadingTotalJobSuccessRate}
            />
            <ProfileStat
              label={workLabel}
              value={`${dataTotalJobHired?.total || 0}`}
              isLoading={isLoadingTotalJobHired}
            />
            <ProfileStat
              label={totalEarnedLabel}
              value={`$${formatCash(Number(dataTotalEarned?.total || 0))}`}
              isLoading={isLoadingTotalEarned}
            />
            <ProfileStat
              label={workStatusLabel}
              value={
                data?.talent?.work_status == UserWorkStatus.SOLE_PROPRIETOR
                  ? soleProprietorLabel
                  : data?.talent?.work_status == UserWorkStatus.ENTREPRENEUR
                    ? entrepreneurLabel
                    : data?.talent?.work_status == UserWorkStatus.PART_TIME
                      ? partTimeLabel
                      : notSetLabel
              }
            />
          </Stack>
        </Stack>
        <Stack
          direction="row"
          sx={{
            ml: { xs: 0, sm: 2 },
            mt: { xs: 2, sm: 0 },
            width: { xs: "100%", sm: "auto" }
          }}
          spacing={1}
          alignItems="center"
        >
          <LoadingButton
            variant={isFavorite ? "contained" : "outlined"}
            color={isFavorite ? "error" : "success"}
            loading={addingFavorite || deletingFavorite}
            onClick={isFavorite ? onDeleteFav : onAddFav}
            startIcon={<Favorite />}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              flex: { xs: 1, sm: "none" },
            }}
          >
            {isFavorite ? favoritedLabel : addAsFavoriteLabel}
          </LoadingButton>
          <MoreMenu talent={data} />
        </Stack>
      </Stack>
    </CardBase>
  );
};

const TalentDetailPage = () => {
  const { talentId } = useParams();
  const successfullyAddAsFavoriteLabel = useUILabel("talent.successfullyAddAsFavorite");
  const successfullyRemoveFromFavoriteLabel = useUILabel("talent.successfullyRemoveFromFavorite");

  const [doAddFavorite, { isLoading: addingFavorite }] =
    useAddFavoriteTalentMutation();
  const [doDeleteFavorite, { isLoading: deletingFavorite }] =
    useDeleteFavoriteTalentMutation();

  const handleAddFav = async () => {
    if (talentId) {
      try {
        await doAddFavorite(parseInt(talentId!)).unwrap();
        toast.success(successfullyAddAsFavoriteLabel);
      } catch (err: any) {
        toast.error(err);
      }
    }
  };

  const handleDeleteFav = async () => {
    if (favId) {
      try {
        await doDeleteFavorite(favId).unwrap();
        toast.success(successfullyRemoveFromFavoriteLabel);
      } catch (err: any) {
        toast.error(err);
      }
    }
  };

  const { data } = useGetTalentByIdQuery(parseInt(talentId!), {
    skip: !talentId,
  });

  const { data: favorites } = useGetAllFavoriteTalentsQuery({});

  const { isFavorite, favId } = React.useMemo(() => {
    const found = favorites?.find(
      (item) => item.user.id === parseInt(talentId!)
    );
    return { isFavorite: !!found, favId: found?.id || 0 };
  }, [favorites, talentId]);

  useTitle(
    `Independent Profesional #${data?.id}`,
    `${data?.first_name} ${data?.last_name}`
  );

  const {
    data: dataTotalJobSuccessRate,
    isLoading: isLoadingTotalJobSuccessRate,
  } = useGetTalentTotalJobSuccessRateByIdQuery(parseInt(talentId!), {
    skip: !talentId,
  });

  const { data: dataTotalJobHired, isLoading: isLoadingTotalJobHired } =
    useGetTalentTotalJobHiredByIdQuery(parseInt(talentId!), {
      skip: !talentId,
    });

  const { data: dataTotalEarned, isLoading: isLoadingTotalEarned } =
    useGetTalentTotalEarnedByIdQuery(parseInt(talentId!), {
      skip: !talentId,
    });

  // Get only insurance and legal_id documents
  const {
    data: insuranceDocuments,
    isLoading: isLoadingInsurance,
  } = useGetUserDocumentsQuery(
    { userId: parseInt(talentId!), documentType: "insurance" },
    {
      skip: !talentId,
    }
  );

  const {
    data: legalIdDocuments,
    isLoading: isLoadingLegalId,
  } = useGetUserDocumentsQuery(
    { userId: parseInt(talentId!), documentType: "legal_id" },
    {
      skip: !talentId,
    }
  );

  // Combine insurance and legal_id documents
  const documents = React.useMemo(() => {
    const insurance = insuranceDocuments || [];
    const legalId = legalIdDocuments || [];
    return [...insurance, ...legalId];
  }, [insuranceDocuments, legalIdDocuments]);

  const isLoadingDocuments = isLoadingInsurance || isLoadingLegalId;

  const driverLicenseLabel = useUILabel("talent.driverLicense");
  const workPermitLabel = useUILabel("talent.workPermit");
  const insuranceLabel = useUILabel("talent.insurance");

  const getDocumentTypeLabel = (docType?: string) => {
    if (!docType) return "Document";
    const labels: { [key: string]: string; } = {
      // Legal ID sub-types
      driver_license: driverLicenseLabel,
      passport: "Passport",
      state_id: "State ID",
      work_permit: workPermitLabel,
      other: "Other",
      // Document types
      legal_id: "Legal ID",
      insurance: insuranceLabel,
    };
    return labels[docType] || docType.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  return (
    <Box sx={{ px: { xs: 0, sm: 3 }, py: 3 }}>
      <ProfileHeader
        data={data}
        dataTotalJobSuccessRate={dataTotalJobSuccessRate}
        dataTotalJobHired={dataTotalJobHired}
        dataTotalEarned={dataTotalEarned}
        isLoadingTotalJobSuccessRate={isLoadingTotalJobSuccessRate}
        isLoadingTotalJobHired={isLoadingTotalJobHired}
        isLoadingTotalEarned={isLoadingTotalEarned}
        addingFavorite={addingFavorite}
        deletingFavorite={deletingFavorite}
        isFavorite={isFavorite}
        onAddFav={handleAddFav}
        onDeleteFav={handleDeleteFav}
      />
      <TalentProfilePanel
        data={data}
        documents={documents}
        isLoadingDocuments={isLoadingDocuments}
        getDocumentTypeLabel={getDocumentTypeLabel}
      />
    </Box>
  );
};
export default TalentDetailPage;
